import * as React from 'react'
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs"
import styled, { css } from 'styled-components'
import Metadata from '../components/Metadata/Metadata'
import Avatar from '../components/Avatar/Avatar'
import NewsCenterController from '../controllers/NewsCenterController'
import { useTranslation } from 'react-i18next'
import { News as NewsType, NewsCenterMessengerEventType } from '../../types'
import { useSelector } from 'react-redux'
import { AppState } from '../store'
import { useParams } from 'react-router-dom'
import MobilityHelper from '../../helpers/MobilityHelper'
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView'
import { overviewLink, newsLink } from '../helpers/NavigationHelper'
import moment from '../../utilities/Moment'
import TableOfContent from '../components/TableOfContent/TableOfContent'
import { Helmet } from 'react-helmet'
import NewsCenterHelper from '../../helpers/NewsCenterHelper'
import NewsContent from '../components/News/NewsContent'
import NewsSkeleton from '../components/Skeleton/NewsSkeleton'
import CoverImageContainer from '../../components/News/CoverImageContainer'
import CoverImage from '../../components/News/CoverImage'
import NewsTags from '../../components/News/NewsTags'
import NewsTagItem from '../../components/News/NewsTagItem'

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;

  ${NewsContent} {
    padding-top: 20px;
    margin-top: 20px;
  }
`

const Overview = styled.article`
  display: flex;
  flex-direction: column;

  > {
    h1 {
      color: #1a1a1a;
      line-height: 40px;
      font-weight: 600;
      font-size: 32px;
      margin-bottom: 4px;
    }

    p {
      font-size: 15px;
      margin: 0;
      font-weight: 300;
      color: #737373;
    }
  }
`

const MetadataContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 14px;
`

const Content = styled.div`
  padding: 20px 44px;

  @media screen and (max-width: 600px) {
    padding: 20px 0px;
  }
`

interface IProps { }
interface IState {
  news: NewsType
  didInitialLoad: boolean
}

const News = (props: IProps) => {
  const { currentLocale, newsCenter } = useSelector((state: AppState) => state.newsCenter)
  const embedded = NewsCenterHelper.isEmbedded()
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { id } = useParams<{ id: string }>()

  const { t } = useTranslation()
  const [state, setState] = React.useState<IState>({
    news: null,
    didInitialLoad: false,
  })

  const { news, didInitialLoad } = state

  React.useEffect(() => {
    if (didInitialLoad) {
      NewsCenterHelper.sendParentMessage({ type: NewsCenterMessengerEventType.NEWS_LOADED, })
    }
  }, [didInitialLoad])

  const fetchNews = async () => {
    try {
      const { news, } = await NewsCenterController.getNews(newsCenter.id, id)
      setState({
        news: news,
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  const getBreadcrumbs = () => {
    let breadCrumbs: { label: string, to: string }[] = [
      {
        label: t('Collection::All news'),
        to: overviewLink(currentLocale)
      },
    ]

    let crumbs: { label: string, to: string }[] = []

    breadCrumbs = [
      ...breadCrumbs,
      ...crumbs,
      {
        label: MobilityHelper.getTranslation(currentLocale, news.translations, 'title'),
        to: newsLink(news)
      }
    ]

    return breadCrumbs
  }

  React.useEffect(() => {
    fetchNews().catch(console.error)
  }, [id])

  if (!didInitialLoad) {
    return <NewsSkeleton />
  }

  return (
    <>
      <Helmet>
        <title>{MobilityHelper.getTranslation(currentLocale, news.translations, 'title')}</title>
        <meta name="description" content={MobilityHelper.getTranslation(currentLocale, news.translations, 'summary')} />
      </Helmet>

      <Container>
        <Overview>
          {!embedded && <Breadcrumbs
            breadcrumbs={getBreadcrumbs()}
          />}

          {news.cover_image_url && <CoverImageContainer embedded={embedded}>
            <CoverImage
              src={news.cover_image_url}
            />
          </CoverImageContainer>}

          <Content>
            {news.tags?.length > 0 && <NewsTags style={{ marginBottom: 0 }}>
              {news.tags.map(tag => {
                return (
                  <NewsTagItem
                    key={tag.id}
                  >
                    {tag.name}
                  </NewsTagItem>
                )
              })}
            </NewsTags>}

            <h1>{MobilityHelper.getTranslation(currentLocale, news.translations, 'title')}</h1>

            <Metadata style={{ marginTop: 20 }}>
              {newsCenter.show_news_authors && <Avatar
                name={news?.author?.name || newsCenter.business_name}
                src={news?.author?.avatar_url}
                width={24}
              />}
              <MetadataContent>
                {newsCenter.show_news_authors && <span>
                  {t('News::Shared by {{author}}', { author: news?.author?.name || newsCenter.business_name })} • {moment(news.updated_at).fromNow()}
                </span>}
              </MetadataContent>
            </Metadata>

            <NewsContent ref={contentRef}>
              <p>{MobilityHelper.getTranslation(currentLocale, news.translations, 'summary')}</p>

              <FroalaEditorView
                model={MobilityHelper.getTranslation(currentLocale, news.translations, 'content')}
              />
            </NewsContent>
          </Content>
        </Overview>

        {newsCenter.table_of_contents_enabled && <TableOfContent contentRef={contentRef} />}

      </Container >
    </>
  )

}

export default News